import React, { useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface SlideOverProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  /** Panel max szélessége desktopon (Tailwind osztály), mobilon mindig teljes szélesség */
  maxWidthClass?: string;
}

/**
 * Jobbról beúszó részletnézet-panel. Mobilon teljes képernyős, desktopon
 * oldalpanelként jelenik meg. Mindig a DOM-ban marad, hogy a be-/kiúszás animálható legyen.
 */
const SlideOver: React.FC<SlideOverProps> = ({
  isOpen,
  onClose,
  title,
  children,
  maxWidthClass = 'sm:max-w-lg',
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  return (
    <div
      className={`fixed inset-0 z-50 ${isOpen ? '' : 'pointer-events-none'}`}
      aria-hidden={!isOpen}
    >
      <div
        className={`absolute inset-0 bg-gray-900/50 transition-[opacity,visibility] duration-300 ${
          isOpen ? 'opacity-100' : 'invisible opacity-0'
        }`}
        onClick={onClose}
      />
      {/* Nyitott állapotban transform-none, hogy a panelen belüli fixed elemek
          (pl. megerősítő modal) a viewporthoz, ne a panelhez igazodjanak */}
      <div
        className={`absolute inset-y-0 right-0 flex w-full flex-col bg-white shadow-xl transition-[transform,visibility] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${maxWidthClass} ${
          isOpen ? 'transform-none' : 'invisible translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-gray-200 px-4">
          <h2 className="truncate text-base font-semibold text-gray-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 pb-safe-4">{isOpen && children}</div>
      </div>
    </div>
  );
};

export default SlideOver;
